import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardMedia,
  CardContent,
  CircularProgress,
  Alert,
  AppBar,
  Toolbar,
  Button,
  Chip,
  Divider,
  CardActionArea,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  IconButton,
  CardActions,
  TextField
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Info as InfoIcon,
  Image as ImageIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  PhotoCamera as PhotoIcon,
  Inventory as InventoryIcon,
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { getItemDetails, getItemImages } from '../api/items';
import ImageQualityModal from '../components/ImageQualityModal';
import ImageUpload from '../components/ImageUpload';
import { uploadAndAnalyze, deleteImage, updateSortOrder } from '../api/imageQuality';

export default function ItemDetail() {
  const navigate = useNavigate();
  const { itemCode } = useParams();
  const [item, setItem] = useState(null);
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [imageToDelete, setImageToDelete] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [imageToEdit, setImageToEdit] = useState(null);
  const [newSortOrder, setNewSortOrder] = useState('');
  const [editError, setEditError] = useState(null);
  const [infoDialogOpen, setInfoDialogOpen] = useState(false);

  useEffect(() => {
    fetchData();
  }, [itemCode]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch item details and images in parallel
      const [itemResult, imagesResult] = await Promise.all([
        getItemDetails(itemCode),
        getItemImages(itemCode)
      ]);

      if (itemResult.success) {
        setItem(itemResult.data);
      } else {
        setError(itemResult.error || 'Failed to fetch item details');
      }

      if (imagesResult.success) {
        setImages(imagesResult.data);
      }
    } catch (err) {
      console.error('Fetch error:', err);
      setError(err.response?.data?.error || err.message || 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleImageClick = (image) => {
    setSelectedImage(image);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedImage(null);
  };

  const handleAnalysisComplete = async () => {
    // Refresh data after analysis
    const imagesResult = await getItemImages(itemCode);
    if (imagesResult.success) {
      setImages(imagesResult.data);
      
      // Update the selectedImage with fresh data
      if (selectedImage) {
        const updatedImage = imagesResult.data.find(
          img => img.item_code === selectedImage.item_code && 
                 img.image_type === selectedImage.image_type && 
                 img.sort_order === selectedImage.sort_order
        );
        if (updatedImage) {
          setSelectedImage(updatedImage);
        }
      }
    }
  };

  const handleOpenUploadDialog = () => {
    setError(null); // Clear any previous errors
    setUploadDialogOpen(true);
  };

  const handleCloseUploadDialog = () => {
    setError(null); // Clear errors when closing
    setUploadDialogOpen(false);
  };

  const handleUploadSuccess = async (file, metadata, onProgress) => {
    try {
      setError(null);

      const result = await uploadAndAnalyze(file, metadata, onProgress);

      if (result.success) {
        setSnackbarMessage('Image uploaded and analyzed successfully!');
        setSnackbarOpen(true);
        setUploadDialogOpen(false);
        // Refresh the images list
        await fetchData();
      } else {
        throw new Error(result.error || 'Upload failed');
      }
    } catch (err) {
      console.error('Upload error:', err);
      const errorMessage = err.response?.data?.error || err.message || 'Upload failed';
      setError(errorMessage);
      throw err;
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleOpenDeleteDialog = (image) => {
    setImageToDelete(image);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setImageToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!imageToDelete) return;

    try {
      setError(null);
      const result = await deleteImage(
        imageToDelete.item_code,
        imageToDelete.image_type,
        imageToDelete.sort_order || 1
      );

      if (result.success) {
        setSnackbarMessage('Image deleted successfully!');
        setSnackbarOpen(true);
        setDeleteDialogOpen(false);
        setImageToDelete(null);
        // Refresh the images list
        await fetchData();
      } else {
        throw new Error(result.error || 'Delete failed');
      }
    } catch (err) {
      console.error('Delete error:', err);
      const errorMessage = err.response?.data?.error || err.message || 'Delete failed';
      setError(errorMessage);
      setDeleteDialogOpen(false);
      setImageToDelete(null);
    }
  };

  const handleOpenEditDialog = (image) => {
    setImageToEdit(image);
    setNewSortOrder(image.sort_order?.toString() || '1');
    setEditError(null);
    setEditDialogOpen(true);
  };

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setImageToEdit(null);
    setNewSortOrder('');
    setEditError(null);
  };

  const handleSaveEditSortOrder = async () => {
    if (!imageToEdit || !newSortOrder) return;

    const parsedNewSortOrder = parseInt(newSortOrder);
    if (isNaN(parsedNewSortOrder) || parsedNewSortOrder < 1) {
      setEditError('Sort order must be a positive number');
      return;
    }

    try {
      setEditError(null);
      const result = await updateSortOrder(
        imageToEdit.item_code,
        imageToEdit.image_type,
        imageToEdit.sort_order || 1,
        parsedNewSortOrder
      );

      if (result.success) {
        setSnackbarMessage('Sort order updated successfully!');
        setSnackbarOpen(true);
        setEditDialogOpen(false);
        setImageToEdit(null);
        setEditError(null);
        // Refresh the images list
        await fetchData();
      } else {
        throw new Error(result.error || 'Update failed');
      }
    } catch (err) {
      console.error('Update error:', err);
      const errorMessage = err.message || 'Failed to update sort order';
      setEditError(errorMessage);
      // Keep dialog open to show error
    }
  };

  const handleOpenInfoDialog = () => {
    setInfoDialogOpen(true);
  };

  const handleCloseInfoDialog = () => {
    setInfoDialogOpen(false);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const getQualityColor = (score) => {
    if (score >= 80) return 'success';
    if (score >= 60) return 'warning';
    return 'error';
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <AppBar position="static">
        <Toolbar variant="dense" sx={{ minHeight: 44 }}>
          <Button
            color="inherit"
            startIcon={<BackIcon />}
            onClick={() => navigate('/items')}
            size="small"
            sx={{ mr: 1 }}
          >
            Back to Items
          </Button>
          <InfoIcon sx={{ mr: 1, fontSize: 18 }} />
          <Typography variant="subtitle1" component="div" sx={{ flexGrow: 1, fontWeight: 600 }}>
            Item Details: {itemCode}
          </Typography>
        </Toolbar>
      </AppBar>

      {error && (
        <Alert severity="error" sx={{ mx: 1.5, mt: 1, py: 0 }}>
          {error}
        </Alert>
      )}

      {item && (
        <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', gap: 1, p: 1 }}>

          {/* ── Left column: Item Info + Stock on Hand ── */}
          <Box sx={{ flex: '0 0 60%', display: 'flex', flexDirection: 'column', gap: 1, overflow: 'hidden' }}>

            {/* Item Information */}
            <Paper sx={{ p: 1.5, overflow: 'auto', flex: '0 0 auto' }}>
              <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, color: 'primary.main', display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                <InfoIcon sx={{ fontSize: 14 }} /> Item Information
              </Typography>
              <Divider sx={{ mb: 1 }} />

              {/* Product Details */}
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: 0.8, textTransform: 'uppercase' }}>
                Product Details
              </Typography>
              <Grid container columnSpacing={1} rowSpacing={0.5} sx={{ mt: 0.25, mb: 1 }}>
                {[
                  { label: 'Item Code', value: <strong>{item.item_code}</strong> },
                  { label: 'Item Name', value: item.item_name },
                  { label: 'Brand', value: item.brand },
                  { label: 'Category', value: item.category },
                  { label: 'Collection', value: item.collection },
                  { label: 'Vendor Season', value: item.vendor_season },
                  { label: 'Gender', value: item.gender },
                  { label: 'Color', value: item.color_detail },
                  { label: 'Frame Shape', value: item.frame_shape },
                  { label: 'AI Frame Shape', value: item.ai_frame_shape
                    ? <Chip label={item.ai_frame_shape} color="secondary" size="small" variant="outlined" sx={{ fontWeight: 600, height: 18, fontSize: '0.7rem' }} />
                    : undefined },
                  { label: 'Created Date', value: formatDate(item.created_date) },
                  { label: 'HK Retail Price', value: item.retailPrice_hk != null ? `$${item.retailPrice_hk}` : undefined },
                  {
                    label: 'Images',
                    value: (
                      <Chip
                        label={images.length}
                        color={images.length > 0 ? 'success' : 'default'}
                        variant={images.length > 0 ? 'filled' : 'outlined'}
                        icon={<PhotoIcon style={{ fontSize: 12 }} />}
                        sx={{ height: 18, fontSize: '0.7rem', '& .MuiChip-label': { px: 0.75 } }}
                      />
                    )
                  },
                ].map(({ label, value }) => (
                  <Grid item xs={3} key={label}>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.2 }}>{label}</Typography>
                    <Typography variant="caption" component="div" sx={{ fontWeight: 500, lineHeight: 1.4 }}>{value ?? 'N/A'}</Typography>
                  </Grid>
                ))}
              </Grid>

              <Divider sx={{ my: 0.75 }} />

              {/* Stock & Sales */}
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: 0.8, textTransform: 'uppercase' }}>
                Stock &amp; Sales
              </Typography>
              <Grid container columnSpacing={1} rowSpacing={0.5} sx={{ mt: 0.25, mb: 1 }}>
                {[
                  { label: 'Stock', value: item.bo_stock },
                  { label: 'Hold Qty', value: item.bo_hold_qty },
                  { label: 'Total Sales Qty', value: item.total_sales_item_qty },
                  { label: '1st GR Date', value: formatDate(item.first_gr_date) },
                  { label: 'Last Trx Date', value: formatDate(item.last_trx_date) },
                ].map(({ label, value }) => (
                  <Grid item xs={3} key={label}>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.2 }}>{label}</Typography>
                    <Typography variant="caption" sx={{ fontWeight: 500 }}>{value ?? 'N/A'}</Typography>
                  </Grid>
                ))}
              </Grid>

              <Divider sx={{ my: 0.75 }} />

              {/* Backorder by Region */}
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: 0.8, textTransform: 'uppercase' }}>
                Backorder by Region
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                {[
                  { region: 'HK', boDate: item.hk_last_bo_date, boQty: item.hk_bo_ord_qty },
                  { region: 'CN', boDate: item.cn_last_bo_date, boQty: item.cn_bo_ord_qty },
                  { region: 'SG', boDate: item.sg_last_bo_date, boQty: item.sg_bo_ord_qty },
                ].map(({ region, boDate, boQty }) => (
                  <Box key={region} sx={{ flex: 1, p: 0.75, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700 }}>{region}</Typography>
                    <Box sx={{ display: 'flex', gap: 2, mt: 0.25 }}>
                      <Box>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.2, fontSize: '0.65rem' }}>Last BO Date</Typography>
                        <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>{formatDate(boDate) || 'N/A'}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.2, fontSize: '0.65rem' }}>BO Qty</Typography>
                        <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>{boQty ?? 'N/A'}</Typography>
                      </Box>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Paper>

            {/* Stock on Hand */}
            {item.stock && (
              <Paper sx={{ p: 1.5, flex: 1, overflow: 'auto' }}>
                <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, color: 'primary.main', display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                  <InventoryIcon sx={{ fontSize: 14 }} /> Stock on Hand
                </Typography>
                <Divider sx={{ mb: 1 }} />

                {/* Summary row */}
                {item.stock.summary && item.stock.summary.warehouse_count > 0 && (
                  <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                    {[
                      { label: 'Warehouses', value: item.stock.summary.warehouse_count, color: 'text.primary' },
                      { label: 'Total Stock', value: item.stock.summary.total_stock || 0, color: 'primary.main' },
                      { label: 'Hold Stock', value: item.stock.summary.total_hold_stock || 0, color: 'warning.main' },
                      { label: 'Available', value: item.stock.summary.total_available_stock || 0, color: 'success.main' },
                    ].map(({ label, value, color }) => (
                      <Box key={label} sx={{ flex: 1, p: 0.75, bgcolor: 'grey.50', borderRadius: 1, textAlign: 'center' }}>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.2 }}>{label}</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700, color }}>{value}</Typography>
                      </Box>
                    ))}
                  </Box>
                )}

                {/* Warehouse table */}
                {item.stock.warehouses && item.stock.warehouses.length > 0 ? (
                  <TableContainer>
                    <Table size="small" sx={{ '& .MuiTableCell-root': { py: 0.25, px: 1, fontSize: '0.72rem' } }}>
                      <TableHead>
                        <TableRow sx={{ '& th': { fontWeight: 700 } }}>
                          <TableCell>Code</TableCell>
                          <TableCell>Name</TableCell>
                          <TableCell>Location</TableCell>
                          <TableCell align="right">Stock</TableCell>
                          <TableCell align="right">Hold</TableCell>
                          <TableCell align="right">Avail.</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {item.stock.warehouses.map((warehouse, index) => (
                          <TableRow key={index} hover>
                            <TableCell>{warehouse.wh_code}</TableCell>
                            <TableCell>{warehouse.wh_desc}</TableCell>
                            <TableCell>{warehouse.location || 'N/A'}</TableCell>
                            <TableCell align="right">{warehouse.stock || 0}</TableCell>
                            <TableCell align="right">{warehouse.hold_stock || 0}</TableCell>
                            <TableCell align="right">{warehouse.available_stock || 0}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Typography variant="caption" color="text.secondary">No stock information available.</Typography>
                )}
              </Paper>
            )}
          </Box>

          {/* ── Right column: Images ── */}
          <Paper sx={{ flex: 1, p: 1.5, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, color: 'primary.main', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <PhotoIcon sx={{ fontSize: 14 }} /> Images ({images.length})
              </Typography>
              <Button
                variant="contained"
                size="small"
                startIcon={<UploadIcon sx={{ fontSize: 14 }} />}
                onClick={handleOpenUploadDialog}
                sx={{ py: 0.25, px: 1, fontSize: '0.7rem', minWidth: 0, height: 24 }}
              >
                Upload
              </Button>
            </Box>
            <Divider sx={{ mb: 1 }} />

            {images.length === 0 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1 }}>
                <ImageIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
                <Typography variant="caption" color="text.secondary">No images uploaded yet.</Typography>
              </Box>
            ) : (
              <Grid container spacing={1}>
                {images.map((image, index) => (
                  <Grid item xs={6} sm={4} key={index}>
                    <Card sx={{ height: '100%' }}>
                      <CardActionArea onClick={() => handleImageClick(image)}>
                        <CardMedia
                          component="img"
                          height="130"
                          image={image.s3_url}
                          alt={`${image.item_code} - ${image.image_type}`}
                          sx={{ objectFit: 'contain', backgroundColor: '#f5f5f5' }}
                        />
                        <CardContent sx={{ py: 0.5, px: 1, '&:last-child': { pb: 0.5 } }}>
                          <Typography variant="caption" display="block" noWrap>
                            {image.image_type} {image.sort_order && `(#${image.sort_order})`}
                          </Typography>
                          {image.analyzed_at && image.social_media_ready && (
                            <Chip icon={<CheckIcon />} label="Social Ready" color="success" size="small"
                              sx={{ height: 16, fontSize: '0.6rem', '& .MuiChip-label': { px: 0.5 } }} />
                          )}
                        </CardContent>
                      </CardActionArea>
                      <CardActions sx={{ justifyContent: 'flex-end', pt: 0, pb: 0.5, px: 0.5 }}>
                        <IconButton size="small" color="secondary"
                          onClick={(e) => { e.stopPropagation(); handleOpenEditDialog(image); }}
                          title="Edit Sort Order" sx={{ p: 0.25 }}>
                          <EditIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                        <IconButton size="small" color="error"
                          onClick={(e) => { e.stopPropagation(); handleOpenDeleteDialog(image); }}
                          title="Delete Image" sx={{ p: 0.25 }}>
                          <DeleteIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </CardActions>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </Paper>

        </Box>
      )}
    </Box>

      {/* ── Dialogs & Modals (rendered outside the layout box) ── */}
      {/* Image Quality Modal */}
      {selectedImage && (
        <ImageQualityModal
          open={modalOpen}
          onClose={handleCloseModal}
          image={selectedImage}
          onAnalysisComplete={handleAnalysisComplete}
        />
      )}

      {/* Upload New Image Dialog */}
      <Dialog 
        open={uploadDialogOpen} 
        onClose={handleCloseUploadDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Upload New Image for {itemCode}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              Upload a new image for this item. The system will automatically analyze the quality.
              Select the image type and sort order for organizing multiple images of the same type.
            </Alert>
            {item && (
              <ImageUpload
                onUploadSuccess={handleUploadSuccess}
                onUploadError={(err) => {
                  setError(err.message || 'Upload failed');
                }}
                fixedMetadata={{
                  brand: item.brand,
                  itemCode: item.item_code,
                  type: null  // Allow user to select image type
                }}
                hideFixedFields={true}
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseUploadDialog}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity="success" sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        maxWidth="sm"
      >
        <DialogTitle>Delete Image?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this image?
          </Typography>
          {imageToDelete && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                <strong>Image Type:</strong> {imageToDelete.image_type}
              </Typography>
              {imageToDelete.sort_order && (
                <Typography variant="body2" color="text.secondary">
                  <strong>Sort Order:</strong> {imageToDelete.sort_order}
                </Typography>
              )}
            </Box>
          )}
          <Alert severity="warning" sx={{ mt: 2 }}>
            This action cannot be undone. The image record will be permanently deleted from the database.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Sort Order Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={handleCloseEditDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Sort Order</DialogTitle>
        <DialogContent>
          {editError && (
            <Alert severity="error" sx={{ mt: 2, mb: 2 }} onClose={() => setEditError(null)}>
              {editError}
            </Alert>
          )}
          {imageToEdit && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                <strong>Item Code:</strong> {imageToEdit.item_code}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                <strong>Image Type:</strong> {imageToEdit.image_type}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                <strong>Current Sort Order:</strong> {imageToEdit.sort_order || '1'}
              </Typography>
              
              <TextField
                autoFocus
                margin="dense"
                label="New Sort Order"
                type="number"
                fullWidth
                variant="outlined"
                value={newSortOrder}
                onChange={(e) => setNewSortOrder(e.target.value)}
                inputProps={{ min: 1 }}
                helperText="Enter a positive number (1, 2, 3, etc.)"
                sx={{ mt: 2 }}
              />
              
              <Alert severity="info" sx={{ mt: 2 }}>
                The new sort order must not conflict with existing images of the same type for this item.
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditDialog}>Cancel</Button>
          <Button 
            onClick={handleSaveEditSortOrder} 
            variant="contained" 
            color="primary"
            disabled={!newSortOrder || newSortOrder === (imageToEdit?.sort_order?.toString() || '1')}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Social Media Readiness Info Dialog */}
      <Dialog
        open={infoDialogOpen}
        onClose={handleCloseInfoDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <InfoIcon color="primary" />
            Social Media Readiness Criteria
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" paragraph>
            An image is considered <strong>"Social Media Ready"</strong> when it passes all of the following quality checks:
          </Typography>

          <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: '#f5f5f5' }}>
            <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckIcon fontSize="small" color="success" />
              1. Brightness Level
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ ml: 3 }}>
              Must be between <strong>60-80</strong> (not too dark or overexposed)
            </Typography>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: '#f5f5f5' }}>
            <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckIcon fontSize="small" color="success" />
              2. Sharpness
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ ml: 3 }}>
              Must be at least <strong>70</strong> (clear and not blurry)
            </Typography>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: '#f5f5f5' }}>
            <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckIcon fontSize="small" color="success" />
              3. Contrast
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ ml: 3 }}>
              Must be at least <strong>50</strong> (good separation between subject and background)
            </Typography>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: '#f5f5f5' }}>
            <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckIcon fontSize="small" color="success" />
              4. Product Positioning
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ ml: 3 }}>
              Product must be <strong>centered</strong> (within 15% tolerance) and take up <strong>appropriate space</strong> (9-49% of frame)
            </Typography>
          </Paper>

          <Alert severity="info" sx={{ mt: 3 }}>
            <Typography variant="body2">
              <strong>Overall Score:</strong> The percentage shown is calculated as (passed checks / total checks × 100).
              All 4 checks must pass for an image to be marked as "Social Ready".
            </Typography>
          </Alert>

          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="body2">
              Images that are "Not Ready" can still be used, but may require editing or retaking for optimal social media presentation.
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseInfoDialog} variant="contained">
            Got it
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
